import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useContext, useMemo } from 'react';
import { BoilerAdapter, RelayControl } from 'z3k-config';
import { hasModbusSupport } from 'z3k-parser/model/device-spec/z3k-feature-flags';
import { unpack_validation } from 'z3k-parser/validation/types';
import { findAndChangeLinks } from 'z3k-parser/lib/find-links';
import { useBox } from 'shared/hooks/utils';
import { TurnOn } from '../material-utils/turn-on';
import { Z3KSettings, Z3KSettingsEditor, } from 'console/device-settings/z3k/provider/types';
import { DragDropDrawer } from '../utils/drag-drop-drawer/component';
import { RouteNaming, Z3kSecondRoutes } from 'z3k-settings/routes/type';
import { Z3kDragDropContainer } from '../utils/drag-drop-drawer/container';
import { Box } from '@mui/system';
import { addBuiltInBoilerAdapter } from 'z3k-parser/lib/boiler-adapter';
export function ManagedMechanismsList() {
    const { replace_config, config } = useContext(Z3KSettingsEditor);
    const { device_spec, addIdToBlacklist, sendCommand } = useContext(Z3KSettings);
    const configBox = useBox(config);
    const onAddBoilerAdapter = useCallback(() => {
        replace_config(addBuiltInBoilerAdapter(device_spec, configBox.current));
    }, [device_spec]);
    const onDeleteBoilerAdapter = useCallback((object, new_list) => {
        addIdToBlacklist(object.id);
        replace_config({
            ...findAndChangeLinks(configBox.current, object.id),
            boiler_adapters: new_list,
        });
        sendCommand(object.id, 'RemoveObject', null);
    }, [sendCommand]);
    const showAddBoilerAdapterButton = useMemo(() => {
        if (!device_spec.heating.has_heating) {
            return false;
        }
        const exist_builtin_bas = new Set(BoilerAdapter.get.where(config, (ba) => ba.serial.builtIn));
        if (device_spec.heating.builtin_boiler_adapters.length <= exist_builtin_bas.size) {
            return false;
        }
        return device_spec.heating.builtin_boiler_adapters.length > 0;
    }, [device_spec, config]);
    const onTurnOnAdapter = useCallback((object) => (active) => {
        replace_config(BoilerAdapter.get.modifyById(configBox.current, object.id, (obj) => BoilerAdapter.setEnabled(obj, active)));
    }, [replace_config]);
    const titleComponentRender = useCallback((boiler_adapter) => device_spec.heating.has_heating &&
        device_spec.heating.has_turn_on_boiler_adapter ? (_jsx(TurnOn, { is_active: BoilerAdapter.isEnabled(boiler_adapter), onChange: onTurnOnAdapter(boiler_adapter) })) : null, [device_spec, onTurnOnAdapter]);
    const slotComponentRender = useCallback((boiler_adapter) => {
        const slot = boiler_adapter.slot;
        if (slot === null || slot === undefined) {
            return null;
        }
        return (_jsxs(Box, { sx: {
                flex: 1,
            }, children: ["\u0421\u043B\u043E\u0442 ", slot + (device_spec.io.has_graph_panel_climatic ? 0 : 1)] }));
    }, [device_spec.io.has_graph_panel_climatic]);
    const childrenItem = (selected_item_id) => {
        return (_jsxs(_Fragment, { children: [device_spec.heating.has_heating && (_jsx(DragDropDrawer, { z3k_name: "boiler_adapters", name: RouteNaming[Z3kSecondRoutes.boiler_adapters], help_id: "boiler_adapters", item_url: Z3kSecondRoutes.boiler_adapters, hideAddButton: !showAddBoilerAdapterButton, hideTrashButton: device_spec.heating.has_heating &&
                        !device_spec.heating.show_delete_boiler_adapter_button, enableDnd: true, selected_item_id: selected_item_id, titleComponentRender: titleComponentRender, onDelete: onDeleteBoilerAdapter, onAddClick: onAddBoilerAdapter, detailsComponentRender: slotComponentRender })), device_spec.io.has_relay_controls && (_jsx(DragDropDrawer, { z3k_name: "relay_controls", name: RouteNaming[Z3kSecondRoutes.relay_controls], item_url: Z3kSecondRoutes.relay_controls, help_id: "relay_controls", help_options: {
                        has_heating: device_spec.heating.has_heating,
                        has_io: device_spec.io.has_relay_controls &&
                            !device_spec.io.has_exactly_one_relay_control,
                    }, enableDnd: true, hideAddButton: device_spec.io.has_exactly_one_relay_control &&
                        RelayControl.get.all(config).length >= 1, hideTrashButton: device_spec.io.has_exactly_one_relay_control, selected_item_id: selected_item_id })), device_spec.io.has_pumps && (_jsx(DragDropDrawer, { z3k_name: "pumps", name: RouteNaming[Z3kSecondRoutes.pumps], help_id: "pumps", item_url: Z3kSecondRoutes.pumps, enableDnd: true, selected_item_id: selected_item_id })), device_spec.io.has_threeway_taps && (_jsx(DragDropDrawer, { z3k_name: "threeway_taps", name: RouteNaming[Z3kSecondRoutes.threeway_taps], help_id: "threeway_taps", item_url: Z3kSecondRoutes.threeway_taps, enableDnd: true, selected_item_id: selected_item_id })), device_spec.io.has_modbus && hasModbusSupport(config.device) && (_jsx(DragDropDrawer, { z3k_name: "analog_outputs", name: RouteNaming[Z3kSecondRoutes.analog_outputs], help_id: "analog_outputs", item_url: Z3kSecondRoutes.analog_outputs, enableDnd: true, selected_item_id: selected_item_id }))] }));
    };
    return _jsx(Z3kDragDropContainer, { childrenItem: childrenItem });
}
const fields = [
    'pumps',
    'relay_controls',
    'threeway_taps',
    'boiler_adapters',
    'analog_outputs',
];
ManagedMechanismsList.isError = (validation_result) => fields.reduce((prev, current) => prev || !!unpack_validation(validation_result, current), false);
ManagedMechanismsList.isWarning = ManagedMechanismsList.isError;
ManagedMechanismsList.isShown = (is_service_view, config, spec, is_staff, is_climatic) => is_service_view &&
    !is_climatic &&
    (spec.io.has_relay_controls ||
        spec.io.has_pumps ||
        spec.io.has_threeway_taps ||
        spec.io.show_managed_mechanisms_for_users);
